/**
 * Service para comunicação com API do módulo Customer Support
 */

import { apiService } from '@/lib/api';
import {
  ClientImplementation,
  CreateImplementationDTO,
  UpdateImplementationDTO,
  KanbanData,
  ImplementationPhase,
  ImplementationObservation,
  CreateObservationDTO,
  UpdateObservationDTO,
  ChatConnectSchedule,
  CreateChatConnectScheduleDTO,
  PostImplementationCall,
  CreatePostImplementationCallDTO,
  UpdatePostImplementationCallDTO,
  BusinessRuleItem,
  BusinessRulesAcceptance,
  KanbanColumn,
  CreateKanbanColumnDTO,
  UpdateKanbanColumnDTO,
  ImplementationTraining,
  CreateImplementationTrainingDTO,
} from '@/types/customerSupport';

class CustomerSupportService {
  // ============================================================================
  // IMPLEMENTATIONS
  // ============================================================================

  async getImplementations(filters?: {
    phase?: ImplementationPhase;
    status?: string;
    client_id?: number;
    search?: string;
  }): Promise<ClientImplementation[]> {
    const response = await apiService.get('/customer-support/implementations', { params: filters });
    return response.implementations;
  }

  async getKanban(filters?: {
    status?: string;
  }): Promise<KanbanData> {
    const response = await apiService.get('/customer-support/implementations/kanban', { params: filters });
    return response.kanban;
  }

  async getImplementation(id: number): Promise<ClientImplementation> {
    const response = await apiService.get(`/customer-support/implementations/${id}`);
    return response.implementation;
  }

  async createImplementation(data: CreateImplementationDTO): Promise<ClientImplementation> {
    const response = await apiService.post('/customer-support/implementations', data);
    return response.implementation;
  }

  async updateImplementation(id: number, data: UpdateImplementationDTO): Promise<ClientImplementation> {
    const response = await apiService.put(`/customer-support/implementations/${id}`, data);
    return response.implementation;
  }

  async movePhase(id: number, phase: ImplementationPhase | string): Promise<ClientImplementation> {
    const response = await apiService.put(`/customer-support/implementations/${id}/move-phase`, { phase });
    return response.implementation;
  }

  async deleteImplementation(id: number): Promise<void> {
    await apiService.delete(`/customer-support/implementations/${id}`);
  }

  // ============================================================================
  // KANBAN COLUMNS
  // ============================================================================

  async listKanbanColumns(): Promise<KanbanColumn[]> {
    const response = await apiService.get('/customer-support/kanban-columns');
    return response.columns;
  }

  async createKanbanColumn(data: CreateKanbanColumnDTO): Promise<KanbanColumn> {
    const response = await apiService.post('/customer-support/kanban-columns', data);
    return response.column;
  }

  async updateKanbanColumn(id: number, data: UpdateKanbanColumnDTO): Promise<KanbanColumn> {
    const response = await apiService.put(`/customer-support/kanban-columns/${id}`, data);
    return response.column;
  }

  async deleteKanbanColumn(id: number): Promise<void> {
    await apiService.delete(`/customer-support/kanban-columns/${id}`);
  }

  async reorderKanbanColumns(orderedIds: number[]): Promise<KanbanColumn[]> {
    const response = await apiService.put('/customer-support/kanban-columns/reorder', { order: orderedIds });
    return response.columns;
  }

  // ============================================================================
  // OBSERVATIONS
  // ============================================================================

  async createObservation(implementationId: number, data: CreateObservationDTO): Promise<ImplementationObservation> {
    const response = await apiService.post(`/customer-support/implementations/${implementationId}/observations`, data);
    return response.observation;
  }

  async updateObservation(
    implementationId: number,
    observationId: number,
    data: UpdateObservationDTO
  ): Promise<ImplementationObservation> {
    const response = await apiService.put(
      `/customer-support/implementations/${implementationId}/observations/${observationId}`,
      data
    );
    return response.observation;
  }

  async deleteObservation(implementationId: number, observationId: number): Promise<void> {
    await apiService.delete(`/customer-support/implementations/${implementationId}/observations/${observationId}`);
  }

  // ============================================================================
  // CHATCONNECT SCHEDULES
  // ============================================================================

  async createChatConnectSchedule(implementationId: number, data: CreateChatConnectScheduleDTO): Promise<ChatConnectSchedule> {
    const response = await apiService.post(`/customer-support/implementations/${implementationId}/chatconnect-schedules`, data);
    return response.schedule;
  }

  async deleteChatConnectSchedule(implementationId: number, scheduleId: number): Promise<void> {
    await apiService.delete(`/customer-support/implementations/${implementationId}/chatconnect-schedules/${scheduleId}`);
  }

  // ============================================================================
  // POST IMPLEMENTATION CALLS
  // ============================================================================

  async createPostImplementationCall(implementationId: number, data: CreatePostImplementationCallDTO): Promise<PostImplementationCall> {
    const response = await apiService.post(`/customer-support/implementations/${implementationId}/post-implementation-calls`, data);
    return response.call;
  }

  async updatePostImplementationCall(
    implementationId: number,
    callId: number,
    data: UpdatePostImplementationCallDTO
  ): Promise<PostImplementationCall> {
    const response = await apiService.put(
      `/customer-support/implementations/${implementationId}/post-implementation-calls/${callId}`,
      data
    );
    return response.call;
  }

  async deletePostImplementationCall(implementationId: number, callId: number): Promise<void> {
    await apiService.delete(`/customer-support/implementations/${implementationId}/post-implementation-calls/${callId}`);
  }

  // ============================================================================
  // BUSINESS RULES
  // ============================================================================

  async getBusinessRules(implementationId: number): Promise<BusinessRuleItem[]> {
    const response = await apiService.get(`/customer-support/implementations/${implementationId}/business-rules`);
    return response.rules;
  }

  async saveBusinessRules(implementationId: number, rules: Array<{ client_product_id: number; rule_description: string }>): Promise<BusinessRuleItem[]> {
    const response = await apiService.put(`/customer-support/implementations/${implementationId}/business-rules`, { rules });
    return response.rules;
  }

  async generateBusinessRulesPdf(implementationId: number, interviewDate: string): Promise<{ token: string; link: string }> {
    const response = await apiService.post(`/customer-support/implementations/${implementationId}/business-rules/generate-pdf`, { interview_date: interviewDate });
    return response;
  }

  async getBusinessRulesAcceptance(implementationId: number): Promise<BusinessRulesAcceptance | null> {
    const response = await apiService.get(`/customer-support/implementations/${implementationId}/business-rules/acceptance`);
    return response.acceptance;
  }

  // ============================================================================
  // TRAININGS (agendamentos de treinamento)
  // ============================================================================

  async listTrainings(implementationId: number): Promise<ImplementationTraining[]> {
    const response = await apiService.get(`/customer-support/implementations/${implementationId}/trainings`);
    return response.trainings;
  }

  async createTraining(implementationId: number, data: CreateImplementationTrainingDTO): Promise<ImplementationTraining> {
    const response = await apiService.post(`/customer-support/implementations/${implementationId}/trainings`, data);
    return response.training;
  }

  async deleteTraining(implementationId: number, trainingId: number): Promise<void> {
    await apiService.delete(`/customer-support/implementations/${implementationId}/trainings/${trainingId}`);
  }
}

export const customerSupportService = new CustomerSupportService();

